import React, { FormEvent, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import Appbar from 'components/appbar/Appbar';
import AddressFields from 'components/address/AddressFields';
import InsideSaving from 'components/loading/InsideSaving';
import {
  ContentPad,
  PageShell,
  RecordFooter,
  RecordHeader,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';
import { AddressErrors, emptyAddress, validateAddress } from 'helpers/addressValidation';
import { api } from 'services/api';
import history from 'services/history';
import { Bank } from 'types/bank';
import { Doctor, DoctorAddress, DoctorBank } from 'types/doctor';
import { Specialty } from 'types/specialty';
import DoctorBanks from '../DoctorBanks';
import { buildDoctorPayload } from '../doctorPayload';
import DoctorForm from '../form/DoctorForm';
import DoctorTab from '../tab/DoctorTab';
import { useDoctorValidation } from '../validation/useDoctorValidation';

const NewDoctor: React.FC = () => {
  const [validation, setValidation, validate] = useDoctorValidation();
  const [tab, setTab] = useState('main');
  const [saving, setSaving] = useState(false);
  const [addressErrors, setAddressErrors] = useState<AddressErrors>({});
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [doctor, setDoctor] = useState<Doctor>({
    id: 0,
    email: '',
    name: '',
    phone: '',
    rg: '',
    document_number: '',
    profession: '',
    active: 1,
    address: null,
    birth_date: null,
    crm: '',
    banks: [],
    gender: 'N',
    marital_status: '',
    person_type: 'N',
    specialties: [],
  });
  const [doctorAddress, setDoctorAddress] = useState<DoctorAddress>({ ...emptyAddress, doctor_id: 0 });

  function goBack() {
    history.push('/doctors');
  }

  function handleChange<K extends keyof Doctor>(index: K, value: Doctor[K]) {
    setDoctor(prev => ({ ...prev, [index]: value }));
  }

  function handleAddressChange(address: DoctorAddress) {
    setDoctorAddress(address);
    setAddressErrors({});
  }

  function handleSubmit() {
    setSaving(true);

    api
      .post('/doctors', buildDoctorPayload(doctor, doctorAddress))
      .then(response => {
        toast.success('Médico cadastrado');
        // abre o médico recém-cadastrado, na ficha
        history.push(response.data?.id ? `/doctors/${response.data.id}` : '/doctors');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível cadastrar o médico');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();

    validate(doctor)
      .then(() => {
        const errors = validateAddress(doctorAddress);
        setAddressErrors(errors);

        if (Object.keys(errors).length > 0) {
          setTab('address');
          toast.error('Corrija o endereço para salvar');
          return;
        }

        handleSubmit();
        setValidation({});
      })
      .catch((err: yup.ValidationError) => {
        if (err instanceof yup.ValidationError) {
          setValidation({
            [err.path as string]: err.message,
          });
        }
      });
  }

  // a validação guarda o erro do campo; abre a aba onde ele está
  useEffect(() => {
    if (Object.keys(validation).length > 0) setTab('main');
  }, [validation]);

  useEffect(() => {
    fetch('https://brasilapi.com.br/api/banks/v1')
      .then(response => response.json())
      .then(data => setBanks(data.filter((item: Bank) => !!item.code)))
      .catch(error => console.error('Error fetching bank data:', error));
  }, []);

  useEffect(() => {
    setLoadingSpecialties(true);

    api
      .get('/specialties/all')
      .then(response => setSpecialties(response.data))
      .catch(() => toast.error('Não foi possível carregar as especialidades'))
      .finally(() => setLoadingSpecialties(false));
  }, []);

  // contas ficam na memória até o médico ser salvo
  function handleChangeAddBank(bank: DoctorBank) {
    setDoctor(prev => {
      const existingBank = prev.banks.find(b => b.id === bank.id);
      if (existingBank) {
        return {
          ...prev,
          banks: prev.banks.map(b => (b.id === bank.id ? { ...b, ...bank } : b)),
        };
      }
      return {
        ...prev,
        banks: [...prev.banks, { ...bank, id: prev.banks.length ? Math.max(...prev.banks.map(b => b.id)) + 1 : 1 }],
      };
    });
  }

  function handleDeleteBank(bank: DoctorBank) {
    setDoctor(prev => ({
      ...prev,
      banks: prev.banks.filter(b => b.id !== bank.id),
    }));
  }

  return (
    <>
      <Appbar title="Médicos" backAction={goBack} />

      {saving && <InsideSaving />}

      <PageShell>
        <RecordHeader title="Novo médico" backAction={goBack} />

        <WorkArea>
          <DoctorTab onChange={newTab => setTab(newTab)} tab={tab} />
          <ContentPad>
            {tab === 'banks' ? (
              <DoctorBanks
                banks={banks}
                doctor={doctor}
                handleDeleteBank={handleDeleteBank}
                handleChangeAddBank={handleChangeAddBank}
              />
            ) : (
              <StyledForm onSubmit={handleValidation}>
                {tab === 'address' ? (
                  <AddressFields address={doctorAddress} onChange={handleAddressChange} errors={addressErrors} />
                ) : (
                  <DoctorForm
                    handleChange={handleChange}
                    doctor={doctor}
                    validation={validation}
                    specialties={specialties}
                    loadingSpecialties={loadingSpecialties}
                  />
                )}
                <button type="submit" style={{ display: 'none' }}></button>
              </StyledForm>
            )}
          </ContentPad>
        </WorkArea>

        <RecordFooter>
          <Button variant="contained" size="small" disabled={saving} onClick={() => handleValidation()}>
            Salvar médico
          </Button>
        </RecordFooter>
      </PageShell>
    </>
  );
};

export default NewDoctor;
